import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AssetHistory } from '../../lib/types';

interface AssetHistoryTabProps {
  history: AssetHistory[];
  currency: string;
}

export function AssetHistoryTab({ history, currency }: AssetHistoryTabProps) {
  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount
    );

  return (
    <div className="space-y-6">
      {history.length > 1 ? (
        <div className="h-48 w-full rounded-2xl border border-zinc-100 bg-zinc-50 p-4 dark:border-zinc-900 dark:bg-zinc-950">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e2e8f0"
                opacity={0.1}
              />
              <XAxis
                dataKey="date"
                tickFormatter={(str) =>
                  new Date(str).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })
                }
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide domain={['dataMin - 1000', 'dataMax + 1000']} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  borderRadius: '12px',
                  border: 'none',
                  color: '#fff',
                }}
                itemStyle={{ color: '#fff' }}
                labelStyle={{
                  color: '#a1a1aa',
                  fontSize: '10px',
                  marginBottom: '4px',
                }}
                formatter={(value: number) => [formatMoney(value), 'Value']}
                labelFormatter={(label) => new Date(label).toLocaleDateString()}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorValue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-32 items-center justify-center rounded-2xl border border-zinc-100 bg-zinc-50 p-4 text-center dark:border-zinc-900 dark:bg-zinc-950">
          <p className="text-xs text-zinc-400">
            Not enough history to display chart. Update the asset value to see
            trends.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="px-1 text-xs font-bold tracking-widest text-zinc-400 uppercase">
          History Log
        </h3>
        <div className="space-y-2">
          {history.length === 0 ? (
            <p className="py-4 text-center text-sm text-zinc-500">
              No history records found.
            </p>
          ) : (
            [...history].reverse().map((entry, i) => (
              <div
                key={entry.id}
                className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 p-3 dark:border-zinc-900 dark:bg-zinc-950"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-xs font-bold text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                    {i === 0 ? 'NOW' : i}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
                      {new Date(entry.date).toLocaleDateString()}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      {new Date(entry.date).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
                <p className="font-bold text-zinc-900 dark:text-zinc-100">
                  {formatMoney(entry.value)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
